import type { ReactNode } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Code2,
  Copy,
  Gauge,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { StatusBadge } from "@/components/status-badge";
import { useLocalization } from "@/context/localization-context";
import type { PizzaVerdict } from "@/lib/pizza-verdict";
import { cn } from "@/lib/utils";

export interface DetailNavigation {
  /** 0-based position in the list being browsed, null when unknown */
  position: number | null;
  total: number | null | undefined;
  /** what is counted, e.g. "pizza" / "in order" */
  unit?: string;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  prevLabel?: string;
  nextLabel?: string;
  busy?: boolean;
}

const positionText = (nav: DetailNavigation) =>
  `${nav.position !== null ? nav.position + 1 : "–"} / ${nav.total ?? "–"}`;

export function NavigationGroup({ nav, className }: { nav: DetailNavigation; className?: string }) {
  const { t } = useLocalization();
  const prevLabel = nav.prevLabel ?? t("common.previous");
  const nextLabel = nav.nextLabel ?? t("common.next");
  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={nav.onPrev}
            disabled={!nav.hasPrev}
            aria-label={prevLabel}
          >
            <ChevronLeft />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{prevLabel} (←)</TooltipContent>
      </Tooltip>
      <div className="min-w-16 px-1 text-center text-xs tabular-nums text-muted-foreground">
        {nav.busy ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" /> : positionText(nav)}
        {nav.unit && <div className="text-[10px] leading-3">{nav.unit}</div>}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={nav.onNext}
            disabled={!nav.hasNext}
            aria-label={nextLabel}
          >
            <ChevronRight />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{nextLabel} (→)</TooltipContent>
      </Tooltip>
    </div>
  );
}

/**
 * Bottom bar of phones: previous / next with thumb-sized targets (the header's arrows are out of reach
 * there). The page keeps room for it with `pb-20 md:pb-0`.
 */
export function MobileNavigationBar({ nav }: { nav: DetailNavigation }) {
  const { t } = useLocalization();
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden">
      <div className="mx-auto flex max-w-md items-center gap-2">
        <Button
          variant="outline"
          className="h-11 flex-1"
          onClick={nav.onPrev}
          disabled={!nav.hasPrev}
        >
          <ChevronLeft />
          {nav.prevLabel ?? t("common.previous")}
        </Button>
        <div className="min-w-16 text-center text-xs tabular-nums text-muted-foreground">
          {nav.busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : positionText(nav)}
          {nav.unit && <div className="text-[10px] leading-3">{nav.unit}</div>}
        </div>
        <Button
          variant="outline"
          className="h-11 flex-1"
          onClick={nav.onNext}
          disabled={!nav.hasNext}
        >
          {nav.nextLabel ?? t("common.next")}
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

interface DetailHeaderProps {
  title: string;
  /** pizza type number ("5" of "5. Šunková") */
  typeId?: string | null;
  verdict: PizzaVerdict;
  loading?: boolean;
  /** facts shown under the title (branch, order, time, ...) */
  meta?: ReactNode[];
  back?: { label: string; onClick: () => void };
  nav?: DetailNavigation;
  onReevaluate?: () => void;
  reevaluating?: boolean;
  isDeveloperMode: boolean;
  onDeveloperModeChange: (developer: boolean) => void;
  pizzaId?: string | null;
  onCopyId?: () => void;
}

/** Title row of the pizza detail: what it is, the verdict, browsing and the actions. */
export function DetailHeader({
  title,
  typeId,
  verdict,
  loading,
  meta = [],
  back,
  nav,
  onReevaluate,
  reevaluating,
  isDeveloperMode,
  onDeveloperModeChange,
  onCopyId,
}: DetailHeaderProps) {
  const { t } = useLocalization();
  const verdictLabel =
    verdict.kind === "no_pizza"
      ? t("status.noPizza")
      : verdict.kind === "invalid_photo"
        ? t("status.invalidPhoto")
        : verdict.status === "N/A"
          ? loading
            ? "…"
            : t("status.na")
          : verdict.status === "OK"
            ? t("status.ok")
            : t("status.nok");

  return (
    <header className="flex items-start gap-2 sm:gap-3">
      {back && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="-ml-1 h-9 w-9 shrink-0"
              onClick={back.onClick}
              aria-label={back.label}
            >
              <ArrowLeft />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{back.label}</TooltipContent>
        </Tooltip>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          {typeId && (
            <span className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md bg-muted px-1.5 text-xs font-bold tabular-nums">
              {typeId}
            </span>
          )}
          <h1 className="min-w-0 truncate text-lg font-semibold leading-tight tracking-tight sm:text-xl">
            {title}
          </h1>
          <StatusBadge
            status={verdict.status === "N/A" ? null : verdict.status}
            label={verdictLabel}
            tone={verdict.kind === "ok" ? "ok" : verdict.kind === "not_evaluated" ? "neutral" : "nok"}
            size="md"
            icon
          />
        </div>
        {meta.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:text-sm">
            {meta.map((item, index) => (
              <span key={index} className="flex min-w-0 items-center gap-2">
                {index > 0 && <span aria-hidden className="text-muted-foreground/40">•</span>}
                {item}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        {nav && <NavigationGroup nav={nav} className="mr-1 hidden md:flex" />}
        {onReevaluate && (
          <Button
            variant="outline"
            size="sm"
            className="hidden lg:inline-flex"
            onClick={onReevaluate}
            disabled={reevaluating}
          >
            {reevaluating ? <Loader2 className="animate-spin" /> : <Gauge />}
            {t("detail.header.reevaluate")}
          </Button>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={isDeveloperMode ? "secondary" : "ghost"}
              size="icon"
              className={cn("hidden h-8 w-8 sm:inline-flex", isDeveloperMode && "text-primary")}
              onClick={() => onDeveloperModeChange(!isDeveloperMode)}
              aria-pressed={isDeveloperMode}
              aria-label={t("detail.header.developerMode")}
            >
              <Code2 />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isDeveloperMode ? t("detail.header.developerModeOn") : t("detail.header.developerModeOff")}
          </TooltipContent>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t("detail.header.moreActions")}>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {onReevaluate && (
              <DropdownMenuItem onClick={onReevaluate} disabled={reevaluating}>
                <Gauge className="h-4 w-4" />
                {t("detail.header.reevaluate")}
              </DropdownMenuItem>
            )}
            {onCopyId && (
              <DropdownMenuItem onClick={onCopyId}>
                <Copy className="h-4 w-4" />
                {t("detail.copyPizzaId")}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={isDeveloperMode}
              onCheckedChange={(checked) => onDeveloperModeChange(!!checked)}
            >
              {t("detail.header.developerMode")}
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
